import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Copy, Wand2 } from "lucide-react";
import MarketingNav from "../../components/MarketingNav";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

// Reusable template page. Each entry below becomes an SEO-targeted
// landing page at /templates/:slug, with copy-to-clipboard examples and
// a soft CTA into Rephrase Studio. New templates = new entries here.
// Indexed by route slug.
export const TEMPLATES = {
  "professional-leave-request": {
    title: "Professional leave request email",
    headline: "The leave request",
    italic: "that gets approved.",
    meta: "Write a polished leave request email. Three examples your manager will appreciate.",
    eyebrow: "Career English",
    intent: "leave-request",
    intro: "Whether you're asking for a single day off or two weeks of vacation, a clear leave request lands better than a meandering one. Keep it short, give exact dates, mention coverage, and offer to discuss.",
    samples: [
      {
        title: "Single sick day",
        body: `Subject: Sick leave — today

Hi [Manager],

I'm down with a fever today and will be working from home tomorrow if I'm feeling better. I've handed the standup to [Name] and will respond to urgent Slack messages by end of day.

Thanks,
[Your Name]`,
      },
      {
        title: "Planned holiday (1 week)",
        body: `Subject: Leave request — Aug 12 to Aug 18

Hi [Manager],

I'd like to take leave from Monday August 12 to Sunday August 18 for a family wedding. My current sprint ends on August 9, and I'll hand the [Project] handover to [Name] before I leave.

Happy to discuss if the timing is difficult.

Thanks,
[Your Name]`,
      },
      {
        title: "Personal emergency",
        body: `Subject: Urgent leave — family emergency

Hi [Manager],

I'm dealing with a family emergency and need to step away from work for the next two days (May 15–16). I'll keep my phone on for anything critical. [Name] has the context on [Project] and can cover the standup.

Will share an update tomorrow evening.

Thanks,
[Your Name]`,
      },
    ],
  },
  "hr-follow-up": {
    title: "HR follow-up email after no reply",
    headline: "The follow-up",
    italic: "that doesn't sound annoyed.",
    meta: "A polite, professional HR follow-up email when your query went unanswered. Two examples.",
    eyebrow: "Career English",
    intent: "hr-followup",
    intro: "Following up with HR is a balance — you want a response, but you don't want to sound impatient. Reference the original message, keep it short, and leave room for them to reply without an apology.",
    samples: [
      {
        title: "Follow-up after one week",
        body: `Subject: Following up — offer letter question

Hi [HR Name],

I'm following up on my note from last Monday about clarifying the joining date in the offer letter. I wanted to make sure it didn't get lost.

Could you let me know when I might expect to hear back?

Thanks,
[Your Name]`,
      },
      {
        title: "Follow-up on reimbursement",
        body: `Subject: Reimbursement claim — checking in

Hi [HR Name],

I submitted my travel reimbursement claim (ref #12345) on the 3rd of this month. I haven't seen it in my last salary credit and wanted to check whether anything is pending from my side.

Happy to share any documents again if needed.

Thanks,
[Your Name]`,
      },
    ],
  },
  "salary-negotiation": {
    title: "Salary negotiation email after offer",
    headline: "The negotiation",
    italic: "that doesn't burn the offer.",
    meta: "Negotiate a job offer professionally over email. Two examples that ask for more without sounding ungrateful.",
    eyebrow: "Career English",
    intent: "salary-negotiation",
    intro: "Negotiating an offer in writing is safer than over a call — you control the wording. Anchor on market rate or your specific value, give one specific number, leave the door open. Never lead with thanks-but-no-thanks.",
    samples: [
      {
        title: "Asking for a higher base",
        body: `Subject: Quick question on the offer

Hi [Recruiter],

Thank you for the offer for the [Role] position — I'm genuinely excited about [specific thing about company/team].

I wanted to discuss the base compensation. Based on the role's scope and my experience with [relevant skill], I was hoping we could land closer to ₹[X] base. The rest of the package (equity, bonus, benefits) works well as proposed.

Happy to jump on a quick call if that's easier.

Thanks,
[Your Name]`,
      },
      {
        title: "Asking for joining bonus",
        body: `Subject: One last thing on the offer

Hi [Recruiter],

Thanks again for the offer. The package looks great overall, and I'm leaning toward accepting.

Before I sign, I wanted to ask whether a joining bonus is possible. I'm leaving behind an unvested stock grant of approximately ₹[X] at my current company, and a one-time payment to offset that would let me say yes without hesitation.

Let me know what's feasible.

Thanks,
[Your Name]`,
      },
    ],
  },
  "apology-to-client": {
    title: "Apology email to a client",
    headline: "The apology",
    italic: "that rebuilds trust.",
    meta: "Apologize to a client professionally — own the mistake, offer next steps, don't over-explain.",
    eyebrow: "Career English",
    intent: "apology-client",
    intro: "A real client apology has three parts: own it (no hedging), say what you're doing about it (specifics), and offer a concrete next step. Skip the long story of why it happened — they don't care, and explaining sounds like excusing.",
    samples: [
      {
        title: "Missed deadline",
        body: `Subject: Delay on the [Project] deliverable

Hi [Client],

I'm sorry — the [Project] deliverable we'd committed for today won't be ready until Friday. The delay is on us, not you.

Here's what we're doing: [Name] is taking it over today, and I'm reviewing it with them tomorrow morning so we can send a clean version by Friday EOD. I'll send you a preview Thursday so there are no surprises.

If this timeline doesn't work for your side, please tell me and we'll adjust.

Thanks for your patience,
[Your Name]`,
      },
      {
        title: "Mistake in delivered work",
        body: `Subject: Correction on the report we sent

Hi [Client],

We sent you the Q3 report yesterday, and I noticed this morning that the revenue figure for [Segment] was incorrect — it should be ₹[X], not ₹[Y]. The other numbers are accurate.

I've attached the corrected version. The error came from a stale data source on our end, and we've fixed it so this doesn't repeat.

Sorry for the confusion. Happy to walk through the correct numbers on a call if helpful.

Thanks,
[Your Name]`,
      },
    ],
  },
  "self-introduction-interview": {
    title: "Self introduction for a job interview",
    headline: "The introduction",
    italic: "that earns a follow-up question.",
    meta: "How to introduce yourself in a job interview. Two examples — entry-level and mid-career.",
    eyebrow: "Career English",
    intent: "interview-intro",
    intro: `"Tell me about yourself" is rarely about your life story. Interviewers use it to gauge how you frame your work. Lead with the role you're applying for, then a one-line career arc, then one specific recent accomplishment. Keep it under 90 seconds out loud.`,
    samples: [
      {
        title: "Entry-level (fresh grad)",
        body: `Hi, I'm [Name]. I just graduated with a degree in [Field] from [College], and I'm interviewing for the [Role] position because [one specific reason — the team, the product, the problem].

During college I worked on [specific project] where I [specific action — built X, led Y, learned Z]. The thing I enjoyed most was [insight about the work, not about yourself]. That's what drew me to this role at [Company].

Happy to go deeper into any of that.`,
      },
      {
        title: "Mid-career (3–5 years)",
        body: `I'm [Name]. For the last [N] years I've been a [Role] at [Company], focused on [specific area — payments, data infra, customer growth].

The work I'm proudest of is [specific project]: we [specific outcome — shipped X, reduced Y by Z%]. What I learned from it is [insight — about the work, about working with people, about the industry].

I'm looking at [Company] because [specific reason — the problem the team is solving, the engineering culture, the stage]. Happy to walk through any of the projects in more detail.`,
      },
    ],
  },
  "resignation-letter": {
    title: "Resignation email — two weeks notice",
    headline: "The resignation",
    italic: "that doesn't burn the bridge.",
    meta: "Write a professional resignation email with two weeks notice. Keep it short, gracious, and document it.",
    eyebrow: "Career English",
    intent: "resignation",
    intro: "Resignation is one place where shorter is better. State the decision, give the last day, offer to help with transition. No reasons unless asked. The conversation about why happens in 1:1, not in the email.",
    samples: [
      {
        title: "Standard two-week notice",
        body: `Subject: Resignation — last day [Date]

Hi [Manager],

I'm writing to let you know I'm resigning from my role as [Role]. My last day will be [Date], two weeks from today.

The last [N] years have been a real opportunity, and I'm grateful for everything I learned here. Over the next two weeks I'll work with [Name] to hand over [Project] cleanly. Happy to do more if useful — please let me know what would help.

Thanks,
[Your Name]`,
      },
      {
        title: "Longer notice (1 month)",
        body: `Subject: Resignation — last day [Date]

Hi [Manager],

I've decided to move on from [Company]. My last day will be [Date], giving us about a month to wrap up.

I want to leave [Project] in a good place. Here's what I'd suggest:
- Hand over the [system X] documentation to [Name] this week
- Spend the next two weeks finishing [milestone Y]
- The final week, run knowledge-transfer sessions for the team

Please tell me if you'd prefer a different sequence. I want this to be easy for the team.

Thanks for everything,
[Your Name]`,
      },
    ],
  },
  "project-status-update": {
    title: "Project status update email",
    headline: "The status update",
    italic: "no one wants to skim past.",
    meta: "Write a clear project status update email — green/yellow/red format, concrete next steps, asks at the bottom.",
    eyebrow: "Office English",
    intent: "status-update",
    intro: "Status emails fail when they're a wall of detail. Lead with status (green/yellow/red), then three bullets of substance, then one specific ask. People will read three bullets. They won't read three paragraphs.",
    samples: [
      {
        title: "Weekly project update",
        body: `Subject: [Project] weekly — green

Hi all,

Status this week: green. On track for the May 30 launch.

What shipped:
- Auth integration is live in staging
- [Designer] finalized the onboarding screens
- Test pass: 12/14 critical paths green

What's next:
- Migration script (Wed)
- Internal launch review (Fri)

One ask: I need a final go/no-go from [Stakeholder] by Thursday EOD. Please flag if that's a problem.

Thanks,
[Your Name]`,
      },
      {
        title: "Update with a delay (yellow)",
        body: `Subject: [Project] weekly — yellow (1-week slip)

Hi all,

Status: yellow. Pushing launch from May 30 to June 6, one-week slip.

Why: the API rate-limit issue is taking longer to fix than estimated. We have a working approach, just not yet ready for production.

What changed:
- [Engineer] is full-time on the rate-limit fix this week
- We'll do an internal review Friday and decide between two implementations
- Launch Tuesday June 6 instead of May 30

Risks I'm watching: if the fix doesn't hold under load testing, we may need another week. I'll flag by Wednesday if that becomes likely.

One ask: please move any dependent comms to the new date.

Thanks,
[Your Name]`,
      },
    ],
  },
  "asking-for-help": {
    title: "Asking for help at work — email and Slack",
    headline: "The ask",
    italic: "that doesn't sound stuck.",
    meta: "How to ask for help at work in a way that respects the other person's time. Email and Slack examples.",
    eyebrow: "Office English",
    intent: "ask-help",
    intro: "The worst help-request emails are vague ('can you help with X?'). The best ones show what you tried, where you got stuck, and exactly what you need. Two minutes more on the ask saves twenty minutes of back-and-forth.",
    samples: [
      {
        title: "Slack — quick technical question",
        body: `Hey @[Name] — quick one. I'm trying to deploy the new build, but I'm getting a 502 from the gateway when I hit /api/users. I checked the logs (no errors), restarted the worker (no change), and rolled back to last week's image (works fine).

Do you have 5 minutes today to look at this with me? I'm guessing it's the rate-limit config but I'm not sure.

Cheers!`,
      },
      {
        title: "Email — bigger ask",
        body: `Subject: Quick favor — Q3 numbers review

Hi [Name],

I'm putting together the Q3 board deck and want to make sure the regional revenue split is right before it goes out Friday.

What I'd love: 20 minutes this week to walk you through three slides and get your read. I've already cross-checked against Salesforce, but a second pair of eyes would help.

I can do any time Wed or Thu — what works?

Thanks,
[Your Name]`,
      },
    ],
  },
  "thank-you-email": {
    title: "Thank-you email after a meeting or interview",
    headline: "The thank-you",
    italic: "that the recipient remembers.",
    meta: "Send a thank-you email after a meeting or interview that's specific, brief, and adds value.",
    eyebrow: "Career English",
    intent: "thank-you",
    intro: "A great thank-you is specific (name one thing they said), brief (three sentences), and forward-looking (next step). A generic 'thanks for your time' is worse than not sending one.",
    samples: [
      {
        title: "After an interview",
        body: `Subject: Thank you — great conversation today

Hi [Interviewer],

Thanks for the conversation today. Your point about [specific thing — the team's bet on async-first communication, the tension between speed and reliability] was the most useful thing I've heard from any company this cycle.

I'm even more interested in the role than I was this morning. Happy to provide anything that would help with the next round.

Best,
[Your Name]`,
      },
      {
        title: "After a sales / partner meeting",
        body: `Subject: Thanks — and a quick follow-up

Hi [Name],

Thanks for the time today. I left the conversation thinking about [their specific challenge or insight], and I think there's a real fit here.

Per your ask, I'm attaching the [document]. Let me know when you'd like to reconvene — I'd suggest [specific time / next step].

Best,
[Your Name]`,
      },
    ],
  },
  "linkedin-cold-message": {
    title: "LinkedIn cold message that gets a reply",
    headline: "The cold message",
    italic: "that doesn't get ignored.",
    meta: "Write a LinkedIn cold message that doesn't sound automated. Two openers and one strong follow-up.",
    eyebrow: "Career English",
    intent: "linkedin-cold",
    intro: "LinkedIn cold messages fail when they sound like template form letters. The fix is specificity in line one. Name one thing about them — their post, their company's recent move, a project they shipped. Then a short ask, then a clean exit if they're not interested.",
    samples: [
      {
        title: "Asking for 15 minutes",
        body: `Hi [Name],

I saw your post last week about [specific topic]. The line about [specific quote or idea] is something I've been struggling with at [your company], and it made me rethink the approach.

Would you be open to a 15-minute call sometime in the next two weeks? I'd love to ask you two specific questions about [topic] — not a generic 'pick your brain' chat.

Either way, thank you for writing publicly about this. Rare to see someone be this honest about it.

Best,
[Your Name]`,
      },
      {
        title: "Job inquiry",
        body: `Hi [Name],

I noticed you joined [Company] last month as [Role]. Congrats. I've been following [Company] for a while because of [specific thing — their take on X, their hiring decisions, a product they shipped].

I'm currently a [Role] at [Company] and exploring the [Their Company / Their Team] track. Not asking for a referral — just whether you'd be open to a 20-minute call about what the team is actually like to work on.

If timing isn't right, no worries. Either way, congrats on the move.

Best,
[Your Name]`,
      },
    ],
  },
  "polite-decline": {
    title: "Politely declining at work — emails and replies",
    headline: "The polite no",
    italic: "that doesn't sound defensive.",
    meta: "Decline a meeting, a project, or an extra ask — without burning the relationship. Three examples.",
    eyebrow: "Office English",
    intent: "polite-decline",
    intro: "Saying no at work is a skill. The good version has three parts: acknowledge the request, give your reason briefly (workload, priorities, fit — not personal), and offer a partial alternative if there is one.",
    samples: [
      {
        title: "Declining a meeting request",
        body: `Hi [Name],

Thanks for the invite. I'm trying to keep this week heads-down on [Project] which has a hard deadline Friday, so I'll pass on this meeting.

If a summary would help afterwards, happy to read the notes. And if there's a specific question that needs my input, send it over email and I'll respond Friday afternoon.

Thanks for understanding,
[Your Name]`,
      },
      {
        title: "Declining an extra project",
        body: `Hi [Name],

I appreciate the trust, and this is the kind of work I'd normally raise my hand for. But I have to be honest — between [Project A] and [Project B], adding this would mean one of the three slips.

Would it help if I suggested [colleague] for this one? They've been wanting to take on more.

Happy to discuss if you'd rather reshuffle priorities — let me know.

Thanks,
[Your Name]`,
      },
      {
        title: "Declining a vendor or pitch",
        body: `Hi [Name],

Thanks for the deck and the time today. We've reviewed and decided to go in a different direction this quarter.

To be specific about why so you can adjust: the [specific feature/price/timing] wasn't quite a fit for where we are. That may change next year, and I'll reach out if it does.

Wishing you the best,
[Your Name]`,
      },
    ],
  },
  "professional-bio": {
    title: "Professional bio for LinkedIn or website",
    headline: "The bio",
    italic: "that doesn't sound like a resume.",
    meta: "Write a short professional bio for LinkedIn, conference programs, or your website. Examples at three lengths.",
    eyebrow: "Career English",
    intent: "professional-bio",
    intro: "A bio is not a resume. The good ones lead with what you do today, then one concrete proof point, then one specific interest. Skip 'passionate about'. Skip 'dynamic professional'. Use sentences a human would say.",
    samples: [
      {
        title: "Two sentences (Twitter, conference)",
        body: `[Name] leads [function] at [Company], where she's [specific recent project or focus — built X, scaled Y, shipped Z]. Previously [former role] at [former company]; writes occasionally about [topic] at [link].`,
      },
      {
        title: "One paragraph (LinkedIn About)",
        body: `I'm a [Role] at [Company], focused on [specific area]. Over the last [N] years I've [specific thing 1] and [specific thing 2]. The work I find most interesting sits at the intersection of [A] and [B]. Before [Company] I was at [former company] doing [former role]. Outside work I [one specific interest — running, woodworking, writing about climate policy]. Reach me at [email] or DM me here.`,
      },
      {
        title: "Three paragraphs (personal site)",
        body: `[Name] is a [Role] currently at [Company]. The work focuses on [specific area], with recent projects in [thing 1] and [thing 2].

She started in [first industry/role] at [first company], moved to [second industry/role] at [second company], and has spent the last [N] years in [current focus]. The throughline is [specific theme — making complex tools usable, shipping in regulated industries, scaling small teams].

Outside work, she writes about [specific topic] at [link] and is currently [one current personal project]. Best way to reach her is [email] or LinkedIn.`,
      },
    ],
  },
  "client-onboarding-email": {
    title: "Client onboarding email — first reply",
    headline: "The first email",
    italic: "your client will read three times.",
    meta: "Send a client onboarding email that's warm, professional, and gets the next step moving.",
    eyebrow: "Office English",
    intent: "client-onboard",
    intro: "The first email to a new client sets the relationship. Warmth without performativity, specifics without overload, one clear ask. Skip 'I look forward to our partnership' — say what's happening next instead.",
    samples: [
      {
        title: "Right after contract signing",
        body: `Subject: Welcome — let's start the [Project]

Hi [Client],

Welcome aboard. I'm [Your Name], your point of contact for the [Project] engagement.

Three things to kick off:
1. I'll send the calendar invite for our kickoff call shortly (Thursday 11am works for me — flag if not)
2. By end of this week I'll share the shared workspace link
3. I'll need [specific input from them — brand guidelines, access credentials, the existing copy doc] before the kickoff

If anyone else on your side should be on these emails, please CC them when you reply.

Looking forward to it,
[Your Name]`,
      },
      {
        title: "Inbound lead — first reply",
        body: `Subject: Re: [their subject line]

Hi [Name],

Thanks for reaching out about [their specific ask]. Based on what you've shared, I think we can help — we've done [similar engagement] with [type of client] and the pattern looks comparable.

Two quick questions before I send a proper proposal:
1. What's your target timeline?
2. Who else on your side will be involved in the decision?

If you'd rather chat than email, I have Tuesday 2pm or Thursday 4pm open this week.

Best,
[Your Name]`,
      },
    ],
  },
  "happy-birthday-message": {
    title: "Birthday message to a colleague — professional but warm",
    headline: "The birthday note",
    italic: "that doesn't feel automated.",
    meta: "Write a warm but professional birthday message to a colleague or manager. Three examples for different relationships.",
    eyebrow: "Office English",
    intent: "birthday",
    intro: "Office birthday messages are an underrated relationship investment. They take 90 seconds and people remember them. Specific beats generic. One personal touch beats five generic adjectives.",
    samples: [
      {
        title: "To a close teammate",
        body: `Happy birthday! Hope you're getting a real break today — not the 'two-hour-lunch-still-on-Slack' kind.

Working with you on [recent project] has been one of the highlights of this year. Looking forward to whatever we ship next.

Enjoy the day.`,
      },
      {
        title: "To a manager",
        body: `Happy birthday [Manager]!

Just wanted to say thanks for the [specific thing — feedback on the deck last week, backing me on the X decision, the call on Tuesday]. It made a real difference.

Hope you have a great day off the clock.`,
      },
      {
        title: "To someone you don't know that well",
        body: `Happy birthday [Name]! Hope today brings a quiet hour somewhere good. Looking forward to working together on [upcoming project or thing].

Best,
[Your Name]`,
      },
    ],
  },
};

export default function TemplatePage() {
  const { slug } = useParams();
  const template = TEMPLATES[slug];

  // Update title + meta for SEO when the page mounts.
  useEffect(() => {
    if (!template) return;
    document.title = `${template.title} — SpeakEasy`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = template.meta;
  }, [template]);

  if (!template) return <Navigate to="/templates" replace />;

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "48px 24px 80px" }}>
        <Link to="/templates" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 16 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Templates
        </Link>

        <Chip tone="gold">{template.eyebrow}</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.04, letterSpacing: "-0.025em", margin: "16px 0 16px", fontWeight: 400, maxWidth: "20ch" }}>
          {template.headline} <em style={{ color: "var(--gold-500)" }}>{template.italic}</em>
        </h1>
        <p style={{ fontSize: 18, color: "var(--fg-2)", lineHeight: 1.6, margin: "0 0 32px", maxWidth: "62ch" }}>
          {template.intro}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {template.samples.map((s, i) => <SampleCard key={i} sample={s} />)}
        </div>

        <aside style={{ marginTop: 48, padding: 24, background: "var(--paper-deep)", borderRadius: "var(--radius-lg)", borderTop: "3px solid var(--gold-500)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Wand2 size={18} strokeWidth={1.6} color="var(--gold-500)" />
            <span className="eyebrow">Custom version</span>
          </div>
          <p className="font-display" style={{ fontSize: 26, lineHeight: 1.15, margin: "0 0 12px", fontWeight: 400 }}>
            Want this <em style={{ color: "var(--gold-500)" }}>tailored to you?</em>
          </p>
          <p style={{ color: "var(--fg-2)", fontSize: 15, lineHeight: 1.6, margin: "0 0 16px" }}>
            Paste your specific situation into Rephrase Studio and get the email written for your context. Free, no signup for the first three tones.
          </p>
          <Link to="/write/rephrase" style={{ textDecoration: "none" }}>
            <Button variant="primary">Open Rephrase Studio</Button>
          </Link>
        </aside>
      </main>
    </div>
  );
}

function SampleCard({ sample }) {
  function copy() {
    navigator.clipboard?.writeText(sample.body);
  }
  return (
    <article style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, margin: 0, letterSpacing: "-0.01em" }}>{sample.title}</h3>
        <button type="button" onClick={copy} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", fontSize: 12, fontWeight: 600,
          background: "#fff", color: "var(--fg-2)",
          border: "1px solid var(--border-2)", borderRadius: 999, cursor: "pointer",
        }}>
          <Copy size={12} strokeWidth={1.6} /> Copy
        </button>
      </div>
      <pre style={{
        fontFamily: "var(--font-body)", fontSize: 14.5, lineHeight: 1.65,
        color: "var(--fg-1)", margin: 0, whiteSpace: "pre-wrap",
      }}>{sample.body}</pre>
    </article>
  );
}
